<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-4 mt-5">
      <div class="d-flex align-items-center">
        <h2>Insurance Providers</h2>
        <button @click="addInsuranceProvider" class="btn btn-success ml-4">
          Add Insurance Provider
        </button>
      </div>
      <div style="width: 50%;">
        <SearchBox
          v-model="searchTerm"
          placeholder="Search providers..."
        />
      </div>
    </div>
    <AIVTable
      v-if="filteredInsuranceProviders && filteredInsuranceProviders.length"
      :columns="insuranceProviderColumns"
      :data="filteredInsuranceProviders"
      :pagination="{ total: 0, totalPages: 0, currentPage: 0, perPage: 0 }"
    >
      <template #cell-variations="{ row }">
        <div class="variations-column">
          <div v-if="getVariationsArray(row.variations).length > 0" class="variations-text">
            {{ formatVariationsDisplay(row.variations) }}
          </div>
          <div v-else class="text-muted">
            No variations
          </div>
        </div>
      </template>
      <template #cell-dates="{ row }">
        <div class="dates-column">
          <div class="created-date">Created: {{ formatDate(row.createdAt) }}</div>
          <div class="updated-date">Updated: {{ formatDate(row.updatedAt) }}</div>
        </div>
      </template>
      <template #cell-actions="{ row }">
        <button @click="editInsuranceProvider(row)" class="btn btn-primary btn-sm mr-2">
          Edit
        </button>
        <button @click="removeInsuranceProvider(row)" class="btn btn-danger btn-sm">
          Delete
        </button>
      </template>
    </AIVTable>

    <!-- Insurance Provider Modal -->
    <AIVDialog
      :visible="$modal.state.insuranceProviderModal"
      :title="modalTitle"
      :show-footer="true"
      :custom-class="'insurance-provider-modal'"
      @update:visible="$modal.close('insuranceProviderModal')"
    >
      <!-- Edit/Create Mode -->
      <div v-if="modal.mode === 'edit' || modal.mode === 'create'" class="insurance-provider-form">
        <div class="form-group mb-3">
          <label for="canonicalName" class="form-label">Canonical Name *</label>
          <input
            id="canonicalName"
            v-model="modal.formData.canonical_name"
            type="text"
            class="form-control"
            placeholder="Enter canonical name"
            required
          />
        </div>
        <div class="form-group mb-3">
          <label class="form-label">Variations</label>
          <div class="variations-management">
            <div 
              v-for="(variation, index) in modal.formData.variations" 
              :key="'variation-' + index" 
              class="variation-input-group mb-2"
            >
              <div class="input-group">
                <input
                  :id="'variation-' + index"
                  v-model="modal.formData.variations[index]"
                  type="text"
                  class="form-control"
                  placeholder="Enter variation name"
                  @input="updateVariation(index, $event.target.value)"
                />
                <div class="input-group-append">
                  <button 
                    @click="removeVariation(index)" 
                    class="btn btn-outline-danger btn-sm"
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
            <button 
              @click="addVariation" 
              class="btn btn-outline-secondary btn-sm"
              type="button"
            >
              + Add Variation
            </button>
          </div>
          <small class="form-text text-muted">
            Add alternative names or variations for this insurance provider
          </small>
        </div>
      </div>

      <!-- Remove Mode -->
      <div v-if="modal.mode === 'remove'" class="insurance-provider-remove-confirm">
        <p class="text-center mb-4">
          Are you sure you want to delete the insurance provider 
          <strong>"{{ modal.selectedInsuranceProvider?.canonical_name }}"</strong>?
        </p>
        <p class="text-muted text-center">This action cannot be undone.</p>
      </div>

      <!-- Modal Footer -->
      <template #footer>
        <div class="d-flex justify-content-around">
          <button 
            @click="closeModal" 
            class="btn btn-secondary"
          >
            Cancel
          </button>
          <button 
            v-if="modal.mode === 'edit'"
            @click="saveInsuranceProvider" 
            class="btn btn-primary ml-2"
            :disabled="modal.loading || !modal.formData.canonical_name"
          >
            {{ modal.loading ? 'Saving...' : 'Save' }}
          </button>
          <button 
            v-if="modal.mode === 'create'"
            @click="createInsuranceProvider" 
            class="btn btn-success ml-2"
            :disabled="modal.loading || !modal.formData.canonical_name"
          >
            {{ modal.loading ? 'Creating...' : 'Create Provider' }}
          </button>
          <button 
            v-if="modal.mode === 'remove'"
            @click="confirmRemoval" 
            class="btn btn-danger ml-2"
            :disabled="modal.loading"
          >
            {{ modal.loading ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </template>
    </AIVDialog>

  </div>
</template>

<script>
import InsuranceProvidersAPIService from "@/serviceHandlers/InsuranceProviersAPIservice";
import AIVTable from "@/components/AIVTable.vue";
import AIVDialog from "@/components/AIVDialog.vue";
import SearchBox from "@/components/SearchBox.vue";

const insuranceProvidersAPIService = new InsuranceProvidersAPIService()

export default {
  name: "InsuranceProvidersPage",
  components: {
    AIVTable,
    AIVDialog,
    SearchBox
  },
  data(){
    return {
      searchTerm: '',
      insuranceProviders: [],
      insuranceProviderColumns: [
        { key: 'canonical_name', label: 'Canonical Name' },
        { key: 'variations', label: 'Variations', component: 'variations' },
        { key: 'dates', label: 'Created / Updated', component: 'dates' },
        { 
          key: 'actions', 
          label: 'Actions', 
          sortable: false,
          component: 'div'
        }
      ],
      modal: {
        mode: null, // 'edit', 'remove', or 'create'
        selectedInsuranceProvider: null,
        loading: false,
        formData: {
          canonical_name: '',
          variations: []
        }
      }
    }
  },
  computed: {
    filteredInsuranceProviders() {
      if (!this.insuranceProviders || !this.searchTerm) {
        return this.insuranceProviders || [];
      }
      
      const searchLower = this.searchTerm.toLowerCase();
      return this.insuranceProviders.filter(provider => {
        const matchesCanonicalName = provider.canonical_name?.toLowerCase().includes(searchLower);
        const matchesVariations = this.getVariationsArray(provider.variations).some(variation =>
          variation.toLowerCase().includes(searchLower)
        );
        
        return matchesCanonicalName || matchesVariations;
      });
    },
    modalTitle() {
      if (this.modal.mode === 'edit') {
        return 'Edit Insurance Provider';
      } else if (this.modal.mode === 'remove') {
        return 'Delete Insurance Provider';
      } else if (this.modal.mode === 'create') {
        return 'Create New Insurance Provider';
      }
      return '';
    }
  },
  async mounted(){
    console.clear()
    await this.getInsuranceProviders()
  },
  methods: {
    async getInsuranceProviders(){
      try {
        this.insuranceProviders = await insuranceProvidersAPIService.getInsuranceProviders(this.$router)
        console.log(this.insuranceProviders)
      } catch (e) {
        console.error(e)
      }
    },
    editInsuranceProvider(insuranceProvider) {
      console.log(insuranceProvider)
      this.modal.selectedInsuranceProvider = insuranceProvider;
      this.modal.mode = 'edit';
      this.modal.formData.canonical_name = insuranceProvider.canonical_name;
      this.modal.formData.variations = this.parseVariationsString(insuranceProvider.variations);
      this.$modal.open('insuranceProviderModal');
    },
    removeInsuranceProvider(insuranceProvider) {
      this.modal.selectedInsuranceProvider = insuranceProvider;
      this.modal.mode = 'remove';
      this.$modal.open('insuranceProviderModal');
    },
    addInsuranceProvider() {
      this.modal.mode = 'create';
      this.modal.selectedInsuranceProvider = null;
      this.modal.formData.canonical_name = '';
      this.modal.formData.variations = [];
      this.$modal.open('insuranceProviderModal');
    },
    closeModal() {
      this.$modal.close('insuranceProviderModal');
      this.modal.mode = null;
      this.modal.selectedInsuranceProvider = null;
      this.modal.loading = false;
      this.modal.formData.canonical_name = '';
      this.modal.formData.variations = [];
    },
    async saveInsuranceProvider() {
      this.modal.loading = true;
      try {
        const insuranceProviderData = {
          canonical_name: this.modal.formData.canonical_name,
          variations: this.stringifyVariationsArray(this.modal.formData.variations)
        };
        
        await insuranceProvidersAPIService.updateInsuranceProvider(
          this.modal.selectedInsuranceProvider.id,
          insuranceProviderData,
          this.$router
        );
        
        // Refresh the insurance providers data to show updated info
        await this.getInsuranceProviders();
        this.closeModal();
        
        console.log('Insurance provider updated successfully');
      } catch (error) {
        console.error('Error updating insurance provider:', error);
        alert('Failed to update insurance provider. Please try again.');
      } finally {
        this.modal.loading = false;
      }
    },
    async createInsuranceProvider() {
      this.modal.loading = true;
      try {
        const insuranceProviderData = {
          canonical_name: this.modal.formData.canonical_name,
          variations: this.stringifyVariationsArray(this.modal.formData.variations)
        };
        
        await insuranceProvidersAPIService.createInsuranceProvider(
          insuranceProviderData,
          this.$router
        );
        
        // Refresh the insurance providers data to show the new provider
        await this.getInsuranceProviders();
        this.closeModal();
        
        console.log('Insurance provider created successfully');
      } catch (error) {
        console.error('Error creating insurance provider:', error);
        alert('Failed to create insurance provider. Please try again.');
      } finally {
        this.modal.loading = false;
      }
    },
    async confirmRemoval() {
      this.modal.loading = true;
      try {
        await insuranceProvidersAPIService.deleteInsuranceProvider(
          this.modal.selectedInsuranceProvider.id,
          this.$router
        );
        
        // Refresh the insurance providers data to remove the provider from the list
        await this.getInsuranceProviders();
        this.closeModal();
        
        console.log('Insurance provider deleted successfully');
      } catch (error) {
        console.error('Error deleting insurance provider:', error);
        alert('Failed to delete insurance provider. Please try again.');
      } finally {
        this.modal.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    parseVariationsString(variationsString) {
      if (!variationsString) return [];
      
      try {
        // Try parsing as JSON first
        const parsed = JSON.parse(variationsString);
        if (Array.isArray(parsed)) {
          return parsed.filter(v => v && v.trim());
        }
      } catch (e) {
        // If not JSON, treat as comma-separated or newline-separated
      }
      
      // Split by comma or newline and clean up
      return variationsString
        .split(/[,\n]/)
        .map(v => v.trim())
        .filter(v => v.length > 0);
    },
    stringifyVariationsArray(variationsArray) {
      if (!Array.isArray(variationsArray)) return '';
      
      // Filter out empty variations and trim
      const cleanVariations = variationsArray
        .filter(v => v && v.trim())
        .map(v => v.trim());
      
      // Return as JSON string for backend
      return JSON.stringify(cleanVariations);
    },
    getVariationsArray(variationsString) {
      return this.parseVariationsString(variationsString);
    },
    formatVariationsDisplay(variationsString) {
      const variations = this.getVariationsArray(variationsString);
      if (variations.length === 0) return '';
      
      const displayText = variations.join(', ');
      // Truncate if too long
      if (displayText.length > 100) {
        return displayText.substring(0, 100) + '...';
      }
      return displayText;
    },
    addVariation() {
      this.modal.formData.variations.push('');
    },
    removeVariation(index) {
      this.modal.formData.variations.splice(index, 1);
    },
    updateVariation(index, value) {
      this.$set(this.modal.formData.variations, index, value);
    }
  }
};
</script>

<style scoped>
.dates-column {
  font-size: 0.875rem;
}

.created-date {
  margin-bottom: 2px;
}

.updated-date {
  color: #6c757d;
  font-size: 0.8rem;
}

.variations-column {
  font-size: 0.875rem;
  max-width: 300px;
}

.variations-text {
  word-wrap: break-word;
  line-height: 1.4;
}

.variations-management {
  max-width: 100%;
}

.variation-input-group {
  display: flex;
  align-items: center;
}

.variation-input-group .input-group {
  flex: 1;
}

.variation-input-group .btn {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>